/**
 * OpenStatus Component
 * ====================
 * Displays whether the restaurant is currently open or closed
 * by comparing the current time with the business opening hours.
 */

import { useState, useEffect } from 'react';
import { OPENING_HOURS } from '../config/constants';
import { isUKBankHoliday } from '../utils/bankHolidays';

// Day names mapping (JavaScript getDay() returns 0-6, Sunday-Saturday)
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];


/**
 * Convert time string (HH:MM) to minutes since midnight
 */
const timeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Get current time in minutes since midnight
 */
const getCurrentMinutes = () => {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
};

/**
 * Check if restaurant is currently open
 */
const getOpenStatus = () => {
  const now = new Date();
  const currentDay = DAY_NAMES[now.getDay()];
  const currentMinutes = getCurrentMinutes();

  // Bank holidays override the regular weekly schedule
  const bankHoliday = isUKBankHoliday(now);
  const todayHours = bankHoliday ? OPENING_HOURS.bankHoliday : OPENING_HOURS[currentDay];

  // If closed today
  if (todayHours.closed) {
    return {
      isOpen: false,
      isBankHoliday: false,
      message: 'Closed today',
      nextOpen: getNextOpenTime(now)
    };
  }

  const openMinutes = timeToMinutes(todayHours.open);
  const closeMinutes = timeToMinutes(todayHours.close);

  // Check if currently within opening hours
  if (currentMinutes >= openMinutes && currentMinutes < closeMinutes) {
    // Calculate minutes until closing
    const minutesUntilClose = closeMinutes - currentMinutes;
    const closingSoon = minutesUntilClose <= 60; // Within 1 hour of closing

    return {
      isOpen: true,
      isBankHoliday: bankHoliday,
      closingSoon,
      closingTime: todayHours.close,
      message: closingSoon
        ? `Closing soon at ${formatTime(todayHours.close)}`
        : `Open until ${formatTime(todayHours.close)}`
    };
  }

  // Before opening today
  if (currentMinutes < openMinutes) {
    return {
      isOpen: false,
      isBankHoliday: bankHoliday,
      message: `Opens at ${formatTime(todayHours.open)}`,
      nextOpen: todayHours.open
    };
  }

  // After closing today
  return {
    isOpen: false,
    isBankHoliday: false,
    message: 'Closed',
    nextOpen: getNextOpenTime(now)
  };
};

/**
 * Get next opening time
 */
const getNextOpenTime = (fromDate) => {
  // Check next 7 days using actual dates so bank holidays are detected correctly
  for (let i = 1; i <= 7; i++) {
    const checkDate = new Date(fromDate);
    checkDate.setDate(checkDate.getDate() + i);
    const dayName = DAY_NAMES[checkDate.getDay()];

    if (isUKBankHoliday(checkDate)) {
      const dayDisplayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
      return `${dayDisplayName} at ${formatTime(OPENING_HOURS.bankHoliday.open)}`;
    }

    const dayHours = OPENING_HOURS[dayName];
    if (!dayHours.closed) {
      const dayDisplayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
      return `${dayDisplayName} at ${formatTime(dayHours.open)}`;
    }
  }

  return null;
};

/**
 * Format 24hr time to 12hr format
 */
const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return minutes === 0
    ? `${displayHours}${period}`
    : `${displayHours}:${minutes.toString().padStart(2, '0')}${period}`;
};

/**
 * OpenStatus Badge Component
 */
const OpenStatus = ({ className = '' }) => {
  const [status, setStatus] = useState(getOpenStatus());

  // Update status every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setStatus(getOpenStatus());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {/* Status indicator dot with animation */}
      <span
        className="relative flex h-3 w-3"
        aria-hidden="true"
      >
        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
          status.isOpen
            ? status.closingSoon
              ? 'bg-yellow-400'
              : 'bg-green-400'
            : 'bg-red-400'
        }`} />
        <span
          className={`relative inline-flex rounded-full h-3 w-3 ${
            status.isOpen
              ? status.closingSoon
                ? 'bg-yellow-500'
                : 'bg-green-500'
              : 'bg-red-500'
          }`}
        />
      </span>

      {/* Status text */}
      <span className={`text-sm font-medium ${
        status.isOpen
          ? status.closingSoon
            ? 'text-yellow-400'
            : 'text-green-400'
          : 'text-red-400'
      }`}>
        {status.isOpen
          ? status.closingSoon
            ? 'Closing Soon'
            : 'Open Now'
          : 'Closed'}
      </span>

      {/* Additional info */}
      <span className="text-white/70 text-sm">
        {status.isBankHoliday && (
          <span className="text-amber-bright font-medium">· Bank Holiday </span>
        )}
        {status.message !== 'Closed' && <>· {status.message}</>}
        {!status.isOpen && status.nextOpen && (
          <> · Opens {status.nextOpen}</>
        )}
      </span>
    </div>
  );
};

export default OpenStatus;
